import { RootState, AppDispatch } from '../../store';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";

import { Grid, TextField, Checkbox, FormControlLabel, Button, Link, CircularProgress, Alert, Autocomplete, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, Paper } from '@mui/material';

import { RegisterLayout } from '../../layouts';
import { startRegisterNormalUser } from '../../store/auth';
import { regularExps } from '../../helpers/validators';

import { getColegios, getCursos, getGruposByCurso, getSedesCorreosCR } from '../../api/providers';
import { WhatsApp } from '@mui/icons-material';
import { GrupoInterface } from '../../interfaces';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

type Inputs = {
  correo: string;
  password: string;
  nombre: string; // nombre completo
  primerApellido: string;
  segundoApellido: string;
  tipoCedula: string; // tipo de cédula
  tipoCedulaFacturacion: string; // tipo de cédula
  cedula: string; // número de cédula
  telefono: string;
  telefonoEncargado: string;
  condicionEscolar: string; // condición escolar
  facturaRequerida: boolean; // factura electrónica
  correoFacturacion: string; // correo facturacion
  cedulaFacturacion: string; // cedula Facturacion
  telefonoFacturacion: string; // cedula Facturacion
  colegioId: string; // colegio
  encargadoRetiroNombre: string; // encargado de retirar documentos
  encargadoRetiroTelefono: string;
  sedeCorreosCRId: string; // sede de correos CR
  cursoId: string; // Curso al que desea matricularse
  grupoId: string; // Grupo al que desea pertenecer
  terminos: boolean;
  nombreEncargado: string; // nombre del responsable
  correoEncargado: string; // correo del responsable
};

const validateCedula = (type: string, cedula: string) => {
  switch (type) {
    case 'Fisica':
      return /^[0-9]{1}-[0-9]{4}-[0-9]{4}$/.test(cedula);
    case 'Juridica':
      return /^[0-9]{1}-[0-9]{3}-[0-9]{6}$/.test(cedula);
    case 'DIMEX':
      return /^[0-9]{12}$/.test(cedula);
    case 'NITE':
      return /^[0-9]{10}$/.test(cedula);
    case 'Extranjero':
      return /^[a-zA-Z0-9]{4,20}$/.test(cedula);
    default:
      return false;
  }
};

const formatCedula = (type: string, cedula: string) => {
  if (type === 'Fisica') {
    return cedula.replace(/(\d{1})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (type === 'Juridica') {
    return cedula.replace(/(\d{1})(\d{3})(\d{6})/, '$1-$2-$3');
  }
  return cedula;
};

const unformatCedula = (cedula: string) => {
  return cedula?.replace(/-/g, '');
};

export const RegisterPage = () => {
  const [colegios, setColegios] = useState<any>([]);
  const [cursos, setCursos] = useState<any>([]);
  const [cursoSeleccionado, setCursoSeleccionado] = useState<any>(null);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState<any>(null);
  const [grupos, setGrupos] = useState<any>([]);
  const [sedesCorreosCR, setSedesCorreosCR] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showFacturaFields, setShowFacturaFields] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [provincia, setProvincia] = useState<string>("");
  const [direccionSedeCorreosCR, setDireccionSedeCorreosCR] = useState<string>("");

  useEffect(() => {
    const fetchColegios = async () => {
      setLoading(true);
      const response = await getColegios();
      if (!response.ok) {
        setError('No se pudo cargar los colegios');
        setColegios([]);
      } else {
        setColegios(response.colegios.colegios);
      }
      setLoading(false);
    };

    fetchColegios();
  }, []);

  useEffect(() => {
    const fetchSedeCorreosCR = async () => {
      setLoading(true);
      const response = await getSedesCorreosCR();

      if (!response.ok) {
        setError('No se pudo cargar los colegios');
        setSedesCorreosCR([]);
      } else {
        setSedesCorreosCR(response.sedes.sedeCorreosCR);
      }
      setLoading(false);
    };

    fetchSedeCorreosCR();
  }, []);

  useEffect(() => {
    const fetchCursos = async () => {
      setLoading(true);
      try {
        const response = await getCursos();

        if (response.ok) {
          setCursos(response.cursos.cursos);
        } else {
          setError('No se pudo cargar los cursos');
        }
      } catch (error) {
        setError('Error al cargar cursos');
      }
      setLoading(false);
    };
    fetchCursos();
  }, []);

  useEffect(() => {
    const fetchGrupos = async () => {
      if (!cursoSeleccionado) return;

      setLoading(true);
      try {
        const responseGrupos = await getGruposByCurso(cursoSeleccionado.id);

        if (responseGrupos.ok) {
          setGrupos(responseGrupos.grupos.grupos);
        } else {
          setError('No se pudo cargar los grupos');
          setGrupos([]);
        }
      } catch (error) {
        setError('Hubo un error al cargar los grupos');
        setGrupos([]);
      }
      setLoading(false);
    };

    fetchGrupos();
  }, [cursoSeleccionado]);

  const getCourseById = (id: string) => cursos.find(curso => curso.id === Number(id));
  const getGroupById = (id: string) => grupos.find(grupo => grupo.id === Number(id));

  const dispatch = useDispatch<AppDispatch>();

  const { status, errorMessage } = useSelector((state: RootState) => state.auth);

  const isChecking = status === 'checking';

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<Inputs>({
    defaultValues: {
      correo: '',
      password: '',
      nombre: '',
      primerApellido: '',
      segundoApellido: '',
      telefono: '',
      telefonoEncargado: '',
      condicionEscolar: '',
      facturaRequerida: false,
      colegioId: '',
      encargadoRetiroNombre: '',
      encargadoRetiroTelefono: '',
      sedeCorreosCRId: '',
      cursoId: '',
      grupoId: '',
      terminos: false,
      nombreEncargado: '',
      correoEncargado: ''
    }
  });

  const onSubmit = async (data: Inputs) => {
    const facturacion = {
      tipoCedula: data.tipoCedulaFacturacion,
      cedula: unformatCedula(data.cedulaFacturacion),
      correo: data.correoFacturacion,
      telefono: data.telefonoFacturacion,
    };

    // Create the main data object with the facturacion object included
    const formData = {
      ...data,
      cedula: unformatCedula(data.cedula),
      facturaElectronica: facturacion,
    };

    // Remove facturacion fields from the main data object
    delete formData.tipoCedulaFacturacion;
    delete formData.cedulaFacturacion;
    delete formData.correoFacturacion;
    delete formData.telefonoFacturacion;

    try {
      await dispatch(startRegisterNormalUser(formData));
      setSuccessMessage("Registro exitoso. Revise su correo para más instrucciones.");
    } catch (error) {
      setSuccessMessage(null);
      console.error("Error during registration:", error);
    }
  };

  const termsAccepted = watch("terminos");

  const handleFacturaChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setShowFacturaFields(event.target.value === 'true');
  };

  return (
    <RegisterLayout title="Inscripcion a los cursos y grupos" linkTo="login">
      <form onSubmit={handleSubmit(onSubmit)}>
        {isChecking ? (
          <Grid container alignItems="center" justifyContent="center">

            {successMessage ? (
              <Grid item xs={11} mb={2}>
                <Alert severity='success'>
                  {successMessage}
                </Alert>
              </Grid>
            ) : <CircularProgress style={{ color: '#d32f2f' }} />}


          </Grid>
        ) : (
          <Grid container alignItems="center" justifyContent="center" spacing={1}>
            <Grid item xs={11}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Por favor seleccione el curso y grupo al que desea matricularse
              </Typography>
            </Grid>

            <Grid container display={'flex'} xs={11} spacing={2} my={1}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.cursoId} variant="filled">
                  <InputLabel id="curso-label">Cursos</InputLabel>
                  <Select
                    labelId="curso-label"
                    id="curso-select"
                    {...register("cursoId", { required: "Este campo es requerido" })}
                    value={cursos.length > 0 ? watch("cursoId") : "Sin cursos"}
                    disabled={!(cursos.length > 0)}
                    onChange={(e) => {
                      setValue("cursoId", e.target.value, { shouldValidate: true });
                      setCursoSeleccionado(getCourseById(e.target.value));
                    }}
                  >
                    {cursos.length > 0 ? cursos.map((curso: any) => (
                      <MenuItem key={curso.id} value={curso.id}>
                        {curso.nombre}
                      </MenuItem>
                    )) :
                      <MenuItem key={0} value={"Sin cursos"}>
                        Sin cursos disponibles
                      </MenuItem>}
                  </Select>
                  <FormHelperText>{errors.cursoId?.message}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.grupoId} variant="filled">
                  <InputLabel id="grupo-label">Grupo</InputLabel>
                  <Select
                    labelId="grupo-label"
                    id="grupo-select"
                    {...register("grupoId", { required: "Este campo es requerido" })}
                    value={grupos.length > 0 ? watch("grupoId") : "Sin grupos"}
                    disabled={!(grupos.length > 0)}
                    onChange={(e) => {
                      setValue("grupoId", e.target.value, { shouldValidate: true });
                      setGrupoSeleccionado(getGroupById(e.target.value))
                    }}
                  >
                    {grupos.length > 0 ? grupos.map((grupo: GrupoInterface) => (
                      <MenuItem key={grupo.id} value={grupo.id}>
                        {`${grupo.nombre} / ${grupo.dia} / ${grupo.horaInicio} - ${grupo.horaFin} `}
                      </MenuItem>
                    )) :
                      <MenuItem key={0} value={"Sin grupos"}>
                        Sin grupos disponibles
                      </MenuItem>
                    }

                  </Select>
                  <FormHelperText>{errors.grupoId?.message}</FormHelperText>
                </FormControl>
              </Grid>
              {grupoSeleccionado && grupoSeleccionado.estado == "lleno" && <Grid item xs={12} md={12} mb={2}>
                <Alert severity="error"
                  sx={{
                    fontSize: '1rem',
                    backgroundColor: '#fff4e5', // Fondo amarillo suave
                    color: '#d32f2f',          // Texto en rojo oscuro
                    border: '1px solid #d32f2f', // Borde rojo oscuro
                    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)' // Sombra ligera
                  }}
                >
                  <Typography variant="body1" component="div"
                    sx={{ fontWeight: 'bold', marginBottom: 2, color: '#d32f2f' }}
                  >
                    Atención: Todos los grupos se encuentran llenos.
                  </Typography>

                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    Si decides llenar este formulario, quedarás en lista de espera.
                  </Typography>

                  <ul style={{ paddingLeft: '1.5rem', color: '#d32f2f', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                    <li>La semana del <strong>3 de febrero</strong>, se te comunicará por correo electrónico si se habilitó un espacio en este grupo.</li>
                    <li>Solo puedes llenar el formulario de inscripción para un solo grupo, por lo tanto solo puedes quedar en lista de espera para un único grupo.</li>
                  </ul>

                  <Typography
                    variant="body1"
                    sx={{
                      marginTop: 2,
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: '#d32f2f',
                      textDecoration: 'underline'
                    }}
                  >
                    Si eliges este horario, quedas en lista de espera y no podrás inscribirte en otro grupo del mismo curso.
                  </Typography>
                </Alert>

              </Grid>}
            </Grid>

            <Grid item xs={11} mb={2}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Por favor complete los siguientes datos personales del Estudiante
              </Typography>
            </Grid>

            <Grid container display={'flex'} flexWrap={"wrap"} xs={11} spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Nombre"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("nombre", {
                    required: "Este campo es requerido",
                    minLength: {
                      value: 3,
                      message: "El nombre debe tener al menos 3 caracteres"
                    }
                  })}
                  error={!!errors.nombre}
                  helperText={errors.nombre?.message}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Primer apellido"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("primerApellido", {
                    required: "Este campo es requerido",
                    minLength: {
                      value: 3,
                      message: "El primer apellido debe tener al menos 3 caracteres"
                    }
                  })}
                  error={!!errors.primerApellido}
                  helperText={errors.primerApellido?.message}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Segundo apellido"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("segundoApellido", {
                    required: "Este campo es requerido",
                    minLength: {
                      value: 3,
                      message: "El segundo apellido debe tener al menos 3 caracteres"
                    }
                  })}
                  error={!!errors.segundoApellido}
                  helperText={errors.segundoApellido?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.cedula} variant='filled'>
                  <InputLabel id="cedula-label">Tipo de Cédula del estudiante</InputLabel>
                  <Select
                    labelId="cedula-label"
                    id="cedula-select"
                    label="cedula escolar"
                    {...register("tipoCedula", { required: "Este campo es requerido" })}
                    value={watch("tipoCedula")}
                    onChange={(e) => {
                      setValue("tipoCedula", e.target.value, { shouldValidate: true });
                    }}
                  >
                    <MenuItem value="Fisica">Persona Física</MenuItem>
                    <MenuItem value="Juridica">Persona Jurídica</MenuItem>
                    <MenuItem value="DIMEX">DIMEX</MenuItem>
                    <MenuItem value="NITE">NITE</MenuItem>
                    <MenuItem value="Extranjero">Extranjero(Fuera CR)</MenuItem>
                  </Select>
                  <FormHelperText>{errors.cedula?.message}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Número de cédula del estudiante"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("cedula", {
                    required: "Este campo es requerido",
                    validate: (value) => validateCedula(watch("tipoCedula"), value) || "Formato de cédula inválido",
                    onChange: (e) => {
                      const formattedCedula = formatCedula(watch("tipoCedula"), e.target.value);
                      setValue("cedula", formattedCedula, { shouldValidate: true });
                    }
                  })}
                  error={!!errors.cedula}
                  helperText={errors.cedula?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Correo electrónico del estudiante"
                  type='email'
                  placeholder='ejemplo@correo.com'
                  fullWidth
                  {...register("correo", {
                    required: "Este campo es requerido",
                    pattern: {
                      value: regularExps.email,
                      message: "Este campo debe ser un correo electrónico válido"
                    }
                  })}
                  error={!!errors.correo}
                  helperText={errors.correo?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PhoneInput
                  country={'cr'}
                  value={watch("telefono")}
                  onChange={(phone) => setValue("telefono", `+${phone}`)}
                  inputProps={{
                    name: "telefono",
                    required: true,
                    autoFocus: true
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                />
                {errors.telefono && <FormHelperText error>{errors.telefono.message}</FormHelperText>}
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.condicionEscolar} variant='filled'>
                  <InputLabel id="condicion-label">Condición escolar</InputLabel>
                  <Select
                    labelId="condicion-label"
                    id="condicion-select"
                    label="Condición escolar"
                    {...register("condicionEscolar", { required: "Este campo es requerido" })}
                    value={watch("condicionEscolar")}
                    onChange={(e) => {
                      setValue("condicionEscolar", e.target.value, { shouldValidate: true });
                    }}
                  >
                    <MenuItem value="activo">Activo</MenuItem>
                    <MenuItem value="egresado">Egresado</MenuItem>
                  </Select>
                  <FormHelperText>{errors.condicionEscolar?.message}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={12}>
                <Autocomplete
                  disableClearable
                  options={colegios}
                  getOptionLabel={(colegio) => colegio.nombre}
                  value={colegios.find((colegio: any) => colegio.id === Number(watch("colegioId"))) || null}
                  onChange={(event, colegio) => {
                    setProvincia(colegio ? colegio.provincia : "");
                    setValue("colegioId", colegio ? colegio.id : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Buscar colegio"
                      error={!!errors.colegioId}
                      helperText={errors.colegioId?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  variant='filled'
                  label="Provincia del colegio"
                  type='text'
                  value={provincia}
                  fullWidth
                  disabled={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={11} mb={2}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Datos personales del Encargado (Opcional)
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre del Encargado"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("nombreEncargado")}
                  error={!!errors.nombreEncargado}
                  helperText={errors.nombreEncargado?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Correo del Encargado"
                  type='text'
                  placeholder=''
                  fullWidth
                  {...register("correoEncargado", {
                    pattern: {
                      value: regularExps.email,
                      message: "Este campo debe ser un correo válido"
                    }
                  })}
                  error={!!errors.correoEncargado}
                  helperText={errors.correoEncargado?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneInput
                  country={'cr'}
                  value={watch("telefonoEncargado")}
                  onChange={(phone) => setValue("telefonoEncargado", `+${phone}`)}
                  inputProps={{
                    name: "telefonoEncargado",
                    autoFocus: true
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                />
                {errors.telefonoEncargado && <FormHelperText error>{errors.telefonoEncargado.message}</FormHelperText>}
              </Grid>
              {cursoSeleccionado?.requiereRetiroEnCorreos && (
                <>
                  <Grid item xs={11} mb={2}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Datos del Encargado para retirar el libro
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={12} mb={2}>
                    <Alert severity='warning' sx={{ fontSize: '1rem', backgroundColor: '#e3f2fd' }}>
                      <Typography variant="body1" component="div" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        Atención por favor:
                      </Typography>

                      <ul>
                        <li>No a todos los estudiantes se les envía el libro por Correos de CR.</li>
                        <li>A algunos colegios donde se tiene varios estudiantes se les hace una sola entrega por medio de un coordinador.</li>
                        <li>En algunos lugares del país se les entrega el libro personalmente.</li>
                        <li>Se les estará comunicando a cada uno su respectivo procedimiento de entrega o envío del libro.</li>
                      </ul>

                      <Typography variant="body1" sx={{ marginTop: 2, fontWeight: 'bold' }}>
                        Independientemente de su colegio y/o lugar de residencia, llene por favor los siguientes datos para el envío por Correos de CR:
                      </Typography>
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Autocomplete
                      disableClearable
                      options={sedesCorreosCR}
                      getOptionLabel={(sede: any) => sede.nombre}
                      value={sedesCorreosCR.find((sede: any) => sede.id === Number(watch("sedeCorreosCRId"))) || null}
                      onChange={(event, sede) => {
                        setDireccionSedeCorreosCR(sede ? sede.direccion : "");
                        setValue("sedeCorreosCRId", sede ? sede.id.toString() : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="filled"
                          label="Buscar Sede de correos de Costa Rica"
                          error={!!errors.sedeCorreosCRId}
                          helperText={errors.sedeCorreosCRId?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <TextField
                      variant='filled'
                      label="Direccion de la sede de correos de Costa Rica"
                      type='text'
                      disabled={true}
                      value={direccionSedeCorreosCR}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nombre del Encargado para retirar el libro"
                      type='text'
                      placeholder=''
                      fullWidth
                      {...register("encargadoRetiroNombre", { required: "Este campo es requerido" })}
                      error={!!errors.encargadoRetiroNombre}
                      helperText={errors.encargadoRetiroNombre?.message}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <PhoneInput
                      country={'cr'}
                      value={watch("encargadoRetiroTelefono")}
                      onChange={(phone) => setValue("encargadoRetiroTelefono", `+${phone}`)}
                      inputProps={{
                        name: "encargadoRetiroTelefono",
                        autoFocus: true
                      }}
                      containerStyle={{ width: '100%' }}
                      inputStyle={{ width: '100%', height: '56px' }}
                    />
                    {errors.encargadoRetiroTelefono && <FormHelperText error>{errors.encargadoRetiroTelefono.message}</FormHelperText>}
                  </Grid>

                </>
              )}
              <Grid item xs={11} mb={2}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Información para Factura Electrónica
                </Typography>
              </Grid>

              <Grid item xs={12} md={12}>
                <FormControl fullWidth error={!!errors.facturaRequerida} variant="filled">
                  <InputLabel id="factura-electronica-label">Factura electrónica</InputLabel>
                  <Select
                    labelId="factura-electronica-label"
                    id="factura-electronica-select"
                    value={watch("facturaRequerida") ? "true" : "false"}
                    onChange={(e) => {
                      setValue("facturaRequerida", e.target.value === "true", { shouldValidate: true });
                      handleFacturaChange(e);
                    }}
                  >
                    <MenuItem value="true">Sí, por favor</MenuItem>
                    <MenuItem value="false">No, gracias</MenuItem>
                  </Select>
                  <FormHelperText>{errors.facturaRequerida?.message}</FormHelperText>
                </FormControl>
              </Grid>

              {showFacturaFields && (
                <>


                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.cedula} variant='filled'>
                      <InputLabel id="cedula-label">Tipo de Cédula para la facturación electronica</InputLabel>
                      <Select
                        labelId="cedula-label"
                        id="cedula-select"
                        label="cedula escolar"
                        {...register("tipoCedulaFacturacion", { required: "Este campo es requerido" })}
                        value={watch("tipoCedulaFacturacion")}
                        onChange={(e) => {
                          setValue("tipoCedulaFacturacion", e.target.value, { shouldValidate: true });
                        }}
                      >
                        <MenuItem value="Fisica">Persona Física</MenuItem>
                        <MenuItem value="Juridica">Persona Jurídica</MenuItem>
                        <MenuItem value="DIMEX">DIMEX</MenuItem>
                        <MenuItem value="NITE">NITE</MenuItem>
                        <MenuItem value="Extranjero">Extranjero(Fuera CR)</MenuItem>
                      </Select>
                      <FormHelperText>{errors.tipoCedulaFacturacion?.message}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Cédula para la facturación electronica"
                      type='text'
                      placeholder=''
                      fullWidth
                      {...register("cedulaFacturacion", {
                        required: "Este campo es requerido",
                        validate: (value) => validateCedula(watch("tipoCedulaFacturacion"), value) || "Formato de cédula inválido",
                        onChange: (e) => {
                          const formattedCedula = formatCedula(watch("tipoCedulaFacturacion"), e.target.value);
                          setValue("cedulaFacturacion", formattedCedula, { shouldValidate: true });
                        }
                      })}
                      error={!!errors.cedulaFacturacion}
                      helperText={errors.cedulaFacturacion?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Correo para la facturación electronica"
                      type='email'
                      placeholder='ejemplo@correo.com'
                      fullWidth
                      {...register("correoFacturacion", {
                        required: "Este campo es requerido",
                        pattern: {
                          value: regularExps.email,
                          message: "Este campo debe ser un correo electrónico válido"
                        }
                      })}
                      error={!!errors.correoFacturacion}
                      helperText={errors.correoFacturacion?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PhoneInput
                      country={'cr'}
                      value={watch("telefonoFacturacion")}
                      onChange={(phone) => setValue("telefonoFacturacion", `+${phone}`)}
                      inputProps={{
                        name: "telefonoFacturacion",
                        autoFocus: true
                      }}
                      containerStyle={{ width: '100%' }}
                      inputStyle={{ width: '100%', height: '56px' }}
                    />
                    {errors.telefonoFacturacion && <FormHelperText error>{errors.telefonoFacturacion.message}</FormHelperText>}
                  </Grid>
                </>

              )}
            </Grid>

            <Grid item xs={11} mt={2}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Hace constar que:
              </Typography>
            </Grid>

            <Grid container display={'flex'} xs={11} spacing={2} my={0}>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("terminos", { required: "Debes aceptar los términos y condiciones" })}
                    />
                  }
                  label={
                    <span>
                      La información proporcionada es correcta y completa
                    </span>
                  }
                />
                <br />{errors.terminos && <span style={{ color: '#d32f2f' }}>{errors.terminos.message}</span>}
              </Grid>

              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("terminos", { required: "Debes aceptar los términos y condiciones" })}
                    />
                  }
                  label={
                    <span>
                      Acepto los <Link component={RouterLink} to={`https://academiamejias.com/terminos-condiciones/`} variant="body2">términos y condiciones</Link>
                    </span>
                  }
                />
                <br />{errors.terminos && <span style={{ color: '#d32f2f' }}>{errors.terminos.message}</span>}
              </Grid>

              <Grid item xs={12} md={12} mb={2}>
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                    Antes de finalizar la inscripción, por favor lea la siguiente información:
                  </Typography>
                  <ul>
                    <li>
                      <Typography>
                        Verifique que los datos proporcionados estén correctos.
                      </Typography>
                    </li>
                    <li>
                      <Typography>
                        Al enviar el formulario, recibirá un correo de confirmación con la información para realizar el pago.
                      </Typography>
                    </li>
                    <li>
                      <Typography>
                        La realización del pago quedará habilitada inmediatamente después de completar el registro.
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant="body1" sx={{ marginTop: 2, fontWeight: 'bold' }}>
                    Para más información sobre clases demostrativas:
                  </Typography>
                  <Button component={"a"} target="_blank" href='https://api.whatsapp.com/send/?phone=50688409906&text&type=phone_number&app_absent=0' variant="outlined" color="primary" startIcon={<WhatsApp />} sx={{ fontWeight: 700 }}>
                    Escriba al 8840 9906
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            <Grid item xs={11}>
              <Grid item mb={2} display={errorMessage ? '' : 'none'}>
                <Alert severity='error'>
                  {errorMessage}
                </Alert>
              </Grid>

              <Button
                variant="contained"
                type="submit"
                color='primary'
                fullWidth
                sx={{ borderRadius: 2, padding: 1.5, fontSize: '1.1rem' }}
                disabled={!termsAccepted}
              >
                Enviar la solicitud
              </Button>
            </Grid>
          </Grid>
        )}
      </form>
    </RegisterLayout>
  );
};